<script setup>
import { ref, onMounted } from 'vue'
import { generateToken } from '../utils/auth'
import { fetchUrlSources } from '../utils/common'

const emit = defineEmits(['next-step'])

// 状态变量
const models = ref([])
const selectedModel = ref('')
const modelName = ref('')
const modelDescription = ref('')
const imageUrls = ref([])
const imagePreviews = ref({})
const loading = ref(false)
const taskStatus = ref('')
const token = ref('')
const currentTaskId = ref('')
const loraId = ref('')
const trainingProgress = ref(0)
const sourcePrefix = ref('私密相册')
const statusTaskId = ref('')

// 获取token
async function getToken() {
  token.value = generateToken()
}

// 获取模型列表
async function fetchModels() {
  try {
    const response = await fetch('/mhapi/v1/lora/models', {
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json()
    if (data.data && Array.isArray(data.data)) {
      models.value = data.data
      taskStatus.value = '获取模型列表成功'
    } else {
      throw new Error(data.error.details[1])
    }
  } catch (error) {
    console.error('获取模型列表失败:', error)
    taskStatus.value = '获取模型列表失败: ' + error.message
  }
}

// 处理URL输入
function handleUrlInput(index) {
  const url = imageUrls.value[index]
  if (!url) {
    delete imagePreviews.value[index]
    return
  }
  
  try {
    imagePreviews.value[index] = url
  } catch (error) {
    console.error('URL预览失败:', error)
    taskStatus.value = 'URL预览失败: ' + error.message
  }
}

// 开始训练
async function startTraining() {
  if (!selectedModel.value || imageUrls.value.filter(url => url).length === 0) {
    alert('请选择基础模型并至少添加一张训练图片')
    return
  }

  loading.value = true
  taskStatus.value = '正在提交训练任务...'

  try {
    const requestBody = {
      base_model_id: selectedModel.value,
      model_name: modelName.value || '我的定制模型',
      description: modelDescription.value || '基于个人图片训练的定制模型',
      images: imageUrls.value.filter(url => url)
    }

    const response = await fetch('/mhapi/v1/lora/train', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    })

    const data = await response.json()
    if (data.task_id) {
      currentTaskId.value = data.task_id
      taskStatus.value = '训练任务已提交，正在等待处理...'
      pollTrainingStatus()
    } else {
      throw new Error('创建训练任务失败')
    }
  } catch (error) {
    console.error('提交训练任务失败:', error)
    taskStatus.value = '提交失败: ' + error.message
    loading.value = false
  }
}

// 轮询训练状态
async function pollTrainingStatus() {
  if (!currentTaskId.value) return

  try {
    const response = await fetch(`/mhapi/v1/lora/result/${currentTaskId.value}`, {
      headers: {
        'Authorization': `Bearer ${token.value}`
      }
    })
    const data = await response.json()
    
    trainingProgress.value = data.training_progress * 100
    
    switch (data.state) {
      case 'DONE':
        taskStatus.value = '训练完成！'
        loraId.value = data.lora_id
        loading.value = false
        break
      case 'FAILED':
        taskStatus.value = '训练失败: ' + data.state_msg
        loading.value = false
        break
      case 'PENDING':
      case 'RUNNING':
        taskStatus.value = `训练进行中... ${Math.round(trainingProgress.value)}%`
        setTimeout(pollTrainingStatus, 5*60*1000)
        break
    }
  } catch (error) {
    console.error('查询训练状态失败:', error)
    taskStatus.value = '查询状态失败: ' + error.message
    loading.value = false
  }
}

// 取消训练
async function cancelTraining() {
  if (!currentTaskId.value || !loading.value) return

  try {
    const response = await fetch('/mhapi/v1/lora/cancel', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.value}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ task_id: currentTaskId.value })
    })
    
    const data = await response.json()
    if (data.success) {
      taskStatus.value = '训练已取消'
      loading.value = false
    }
  } catch (error) {
    console.error('取消训练失败:', error)
    taskStatus.value = '取消失败: ' + error.message
  }
}

// 获取素材库图片
async function fetchSourceImages() {
  try {
    loading.value = true
    taskStatus.value = '正在获取素材库...'
    
    const urls = await fetchUrlSources(sourcePrefix.value)
    imageUrls.value = urls
    
    // 预览所有图片
    imagePreviews.value = urls.reduce((acc, url, index) => {
      acc[index] = url
      return acc
    }, {})
    
    taskStatus.value = `已获取 ${urls.length} 张素材图片`
  } catch (error) {
    console.error('获取素材库失败:', error)
    taskStatus.value = '获取素材库失败: ' + error.message
  } finally {
    loading.value = false
  }
}

// 添加新的函数用于手动查询状态
async function checkStatus() {
  if (!statusTaskId.value) {
    alert('请输入任务ID')
    return
  }

  loading.value = true
  taskStatus.value = '正在查询训练状态...'

  try {
    const response = await fetch(`/mhapi/v1/lora/result/${statusTaskId.value}`, {
      headers: {
        'Authorization': `Bearer ${token.value}`
      }
    })
    const data = await response.json()
    
    if (data.state) {
      currentTaskId.value = statusTaskId.value
      trainingProgress.value = data.training_progress * 100
      
      switch (data.state) {
        case 'DONE':
          taskStatus.value = '训练完成！'
          loraId.value = data.lora_id
          break
        case 'FAILED':
          taskStatus.value = '训练失败: ' + data.state_msg
          break
        case 'PENDING':
          taskStatus.value = '任务等待中...'
          break
        case 'RUNNING':
          taskStatus.value = `训练进行中... ${Math.round(trainingProgress.value)}%`
          break
      }
    } else {
      throw new Error('无效的任务状态')
    }
  } catch (error) {
    console.error('查询状态失败:', error)
    taskStatus.value = '查询失败: ' + error.message
  } finally {
    loading.value = false
  }
}

// 组件加载时获取token和模型列表
onMounted(async () => {
  getToken()
  await fetchModels()
})
</script>

<template>
  <div class="video-gen-container">
    <div class="video-gen">
      <div class="section">
        <h2>Lora训练</h2>
        
        <div class="control-panel">
          <div class="input-group">
            <label>选择基础模型（必选）:</label>
            <select v-model="selectedModel">
              <option value="">请选择模型</option>
              <option v-for="model in models" :key="model.id" :value="model.id">
                {{ model.name }}
              </option>
            </select>
            <span class="model-desc" v-if="selectedModel && models.find(m => m.id === selectedModel)">
              {{ models.find(m => m.id === selectedModel).description }}
            </span>
          </div>

          <div class="input-group">
            <label>模型名称（可选）:</label>
            <input 
              type="text" 
              v-model="modelName" 
              placeholder="给你的模型起个名字"
              class="text-input"
              maxlength="64"
            >
          </div>

          <div class="input-group">
            <label>模型描述（可选）:</label>
            <textarea 
              v-model="modelDescription" 
              placeholder="描述一下你的模型特点"
              maxlength="1024"
            ></textarea>
          </div>

          <div class="input-group">
            <label>素材库前缀:</label>
            <div class="source-input-container">
              <input 
                type="text" 
                v-model="sourcePrefix"
                placeholder="输入素材库前缀"
                class="text-input"
              >
              <button 
                @click="fetchSourceImages"
                :disabled="loading"
                class="source-button"
              >
                加载素材
              </button>
            </div>
          </div>

          <div class="input-group">
            <label>查询训练状态:</label>
            <div class="status-input-container">
              <input 
                type="text" 
                v-model="statusTaskId"
                placeholder="输入任务ID"
                class="text-input"
              >
              <button 
                @click="checkStatus"
                :disabled="loading"
                class="source-button"
              >
                查询状态
              </button>
            </div>
          </div>

          <div class="preview-container" v-if="imageUrls.length > 0">
            <div class="preview-grid">
              <div v-for="(url, index) in imageUrls" :key="index" class="preview-item">
                <img :src="url" class="preview-image" :alt="`预览图 ${index + 1}`">
              </div>
            </div>
            <div class="image-count">
              已选择 {{ imageUrls.length }} 张图片
            </div>
          </div>

          <div class="button-group">
            <button 
              @click="startTraining" 
              :disabled="loading || !selectedModel || imageUrls.filter(url => url).length === 0"
              class="primary-button"
            >
              {{ loading ? '训练中...' : '开始训练' }}
            </button>
            <button 
              v-if="loading" 
              @click="cancelTraining"
              class="cancel-button"
            >
              取消训练
            </button>
          </div>
        </div>

        <div class="status-section" v-if="taskStatus">
          <pre class="status-log">
当前任务ID: {{ currentTaskId || '无' }}
{{ taskStatus }}
          </pre>
          <div v-if="loading" class="progress-bar">
            <div 
              class="progress-fill"
              :style="{ width: `${trainingProgress}%` }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 复用 ImageGen 的基础样式 */
.video-gen-container {
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  box-sizing: border-box;
  border-radius: 16px;
}

.video-gen {
  margin: 0 auto;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 15px 50px rgba(0,0,0,0.2);
  backdrop-filter: blur(5px);
  animation: fadeIn 0.5s ease-out;
}

.section {
  margin-bottom: 30px;
  padding: 25px;
  background: var(--section-bg);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: transform 0.3s ease;
  margin-top: 30px;
}

.control-panel {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-bottom: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

label {
  font-weight: 500;
  color: var(--text-color);
}

select, textarea, .text-input {
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  background: var(--input-bg);
  color: var(--text-color);
}

select {
  cursor: pointer;
}

select:hover {
  border-color: var(--primary-color);
}

select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-shadow);
}

.url-input-container {
  width: 100%;
  display: flex;
  gap: 10px;
  align-items: center;
}

.url-input {
  flex: 1;
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  background: var(--input-bg);
  color: var(--text-color);
}

.url-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--focus-shadow);
}

.preview {
  margin-top: 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 10px;
  background: var(--input-bg);
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: contain;
}

button {
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px var(--focus-shadow);
}

.status-log {
  margin: 15px 0;
  padding: 15px;
  background: var(--log-bg);
  color: var(--log-text);
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  max-height: 150px;
  overflow-y: auto;
  font-size: 0.9em;
  line-height: 1.5;
  border: 1px solid var(--border-color);
}

.model-desc {
  font-size: 0.9em;
  color: var(--subtitle-color);
  margin-top: 4px;
  font-style: italic;
}

/* LoraGen 特有的样式 */
.url-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.url-item {
  margin-bottom: 20px;
  padding: 15px;
  background: var(--section-bg);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.add-button {
  padding: 8px 16px;
  font-size: 14px;
}

.remove-button {
  padding: 8px 16px;
  font-size: 14px;
  background: var(--error-color, #dc3545);
}

.button-group {
  display: flex;
  gap: 10px;
}

.progress-bar {
  margin-top: 10px;
  height: 8px;
  background: var(--border-color);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  transition: width 0.3s ease;
}

.status-section {
  margin-top: 20px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.source-input-container {
  display: flex;
  gap: 10px;
  align-items: center;
}

.preview-container {
  margin-top: 20px;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 10px;
}

.preview-item {
  aspect-ratio: 1;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-count {
  margin-top: 10px;
  text-align: right;
  color: var(--subtitle-color);
}

.source-button {
  white-space: nowrap;
}

.status-input-container {
  display: flex;
  gap: 10px;
  align-items: center;
}

.status-input-container .text-input {
  flex: 1;
}

.status-log {
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
}
</style> 